import { useState, useRef, useEffect, type FC, type ReactNode } from "react";
import { CalendarIcon, ChatIcon, PhoneIcon, ChevronLeftIcon, ChevronRightIcon, GlobeIcon, ChevronDownIcon,  CheckIcon, ClockIcon, VideoIcon, HeadphonesIcon, MailIcon} from "./Icons";
import { CheckCircle, SendIcon } from "lucide-react";
import Spinner from "./Components/Spinner";
import { LogoIcon } from "../header/Header";
import {
  createHelpCenterSession,
  getHelpCenterAvailability,
  sendHelpCenterChatMessage,
} from "../lib/api/helpcenter.apis";
import type {
  HelpCenterFormData,
  HelpCenterTimeSlot,
  HelpSessionTypeId,
} from "./helpcenter.types";

// ─── TYPES ────────────────────────────────────────────────────────────────────
interface ChatMessage { id: string; from: "user" | "agent" | "system"; text: string; ts: Date; }
type HelpTab = "schedule" | "chat" | "contact";

// ─── CONSTANTS ────────────────────────────────────────────────────────────────
const BUSINESS = {
  name: "Gemio Support",
  email: "bussiness.hiddengems@gmail.com",
  phone: "+254787819060",
  hours: {
    weekdays: { label: "Monday – Friday", nairobi: "11:00 – 21:00", cet: "09:00 – 21:00" },
    saturday: { label: "Saturday",        nairobi: "12:30 – 21:00", cet: "10:30 – 19:00" },
  },
  timezone: "Africa/Nairobi",
  sessionDuration: 45,
};

const SESSION_TYPES: Array<{ id: HelpSessionTypeId; label: string; desc: string; duration: number; icon: string }> = [
  { id: "demo",     label: "Product Demo",       desc: "See Gemio in action — tailored to your restaurant",    duration: 45, icon: "🎯" },
  { id: "onboard",  label: "Onboarding Call",     desc: "Get set up quickly with a dedicated specialist",       duration: 60, icon: "🚀" },
  { id: "support",  label: "Technical Support",   desc: "Troubleshoot an issue with screen sharing",            duration: 30, icon: "🔧" },
  { id: "strategy", label: "Strategy Session",    desc: "Discuss your restaurant's reservation strategy",       duration: 60, icon: "📊" },
];

const TIMEZONES = [
  { label: "East Africa Time (EAT)",       tz: "Africa/Nairobi",    offset: "+03:00" },
  { label: "Central European Time (CET)",  tz: "Europe/Berlin",     offset: "+01:00" },
  { label: "UTC",                          tz: "UTC",               offset: "+00:00" },
  { label: "Eastern Time (ET)",            tz: "America/New_York",  offset: "-05:00" },
  { label: "Pacific Time (PT)",            tz: "America/Los_Angeles", offset: "-08:00" },
];

const DAYS_SHORT = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"];
const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];

// ─── HELPERS ──────────────────────────────────────────────────────────────────
function getDaysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}
function getFirstDayOfMonth(year: number, month: number): number {
  const day = new Date(year, month, 1).getDay();
  return day === 0 ? 6 : day - 1; // Mon=0
}
function isWeekend(year: number, month: number, day: number): boolean {
  const d = new Date(year, month, day).getDay();
  return d === 0; // Sunday only blocked (Saturday limited hours)
}
function now(): string {
  return new Date().toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", timeZone: BUSINESS.timezone });
}
function isWithinHours(): boolean {
  const hour = new Date().toLocaleTimeString("en-GB", { hour: "2-digit", timeZone: BUSINESS.timezone });
  const h = parseInt(hour);
  const day = new Date().getDay();
  if (day === 0) return false; // Sunday closed
  if (day === 6) return h >= 12 && h < 21;
  return h >= 11 && h < 23;
}

// ─── STEP INDICATOR ──────────────────────────────────────────────────────────
const StepDot: FC<{ step: number; current: number; label: string }> = ({ step, current, label }) => (
  <div className="flex flex-col items-center gap-1.5">
    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all
      ${step < current ? "bg-orange-500 text-white" : step === current ? "bg-orange-500 text-white ring-4 ring-orange-100" : "bg-gray-100 text-gray-400"}`}>
      {step < current ? "✓" : step}
    </div>
    <span className={`text-[10px] font-bold uppercase tracking-wide ${step <= current ? "text-orange-500" : "text-gray-300"}`}>{label}</span>
  </div>
);

// ─── MINI CALENDAR (production-grade) ────────────────────────────────────────
const BookingCalendar: FC<{
  selected: Date | null;
  onSelect: (d: Date) => void;
  bookedDays?: number[];
}> = ({ selected, onSelect, bookedDays = [] }) => {
  const today = new Date();
  const [view, setView] = useState({ year: today.getFullYear(), month: today.getMonth() });

  const daysInMonth = getDaysInMonth(view.year, view.month);
  const firstDay    = getFirstDayOfMonth(view.year, view.month);

  const cells: (number | null)[] = [
    ...Array(firstDay).fill(null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ];

  const isPast = (d: number) => new Date(view.year, view.month, d) < new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const isToday = (d: number) => view.year === today.getFullYear() && view.month === today.getMonth() && d === today.getDate();
  const isSel   = (d: number) => selected && selected.getFullYear() === view.year && selected.getMonth() === view.month && selected.getDate() === d;
  const hasBooking = (d: number) => bookedDays.includes(d);

  const prevMonth = () => {
    if (view.month === 0) setView({ year: view.year - 1, month: 11 });
    else setView(v => ({ ...v, month: v.month - 1 }));
  };
  const nextMonth = () => {
    if (view.month === 11) setView({ year: view.year + 1, month: 0 });
    else setView(v => ({ ...v, month: v.month + 1 }));
  };

  return (
    <div className="w-full select-none">
      {/* Month nav */}
      <div className="flex items-center justify-between mb-6">
        <button onClick={prevMonth}
          className="w-9 h-9 rounded-full flex items-center justify-center bg-gray-100 hover:bg-gray-200 border-none cursor-pointer text-gray-500 transition-colors">
          <ChevronLeftIcon />
        </button>
        <span className="text-base font-bold text-stone-900">{MONTHS[view.month]} {view.year}</span>
        <button onClick={nextMonth}
          className="w-9 h-9 rounded-full flex items-center justify-center bg-blue-100 hover:bg-blue-200 border-none cursor-pointer text-blue-600 transition-colors">
          <ChevronRightIcon />
        </button>
      </div>

      {/* Day headers */}
      <div className="grid grid-cols-7 mb-2">
        {DAYS_SHORT.map(d => (
          <div key={d} className="text-center text-[11px] font-bold text-gray-400 tracking-wider py-1">{d}</div>
        ))}
      </div>

      {/* Day cells */}
      <div className="grid grid-cols-7 gap-y-1">
        {cells.map((day, i) => {
          if (!day) return <div key={`e-${i}`} />;
          const past    = isPast(day);
          const weekend = isWeekend(view.year, view.month, day);
          const disabled = past || weekend;
          const sel     = isSel(day);
          const tod     = isToday(day);
          const booked  = hasBooking(day);

          return (
            <div key={day} className="flex flex-col items-center">
              <button
                disabled={disabled}
                onClick={() => !disabled && onSelect(new Date(view.year, view.month, day))}
                className={`w-10 h-10 rounded-full text-sm font-medium border-none cursor-pointer transition-all relative
                  ${disabled ? "text-gray-300 cursor-default bg-transparent" :
                    sel ? "bg-blue-600 text-white font-bold shadow-md" :
                    tod ? "bg-blue-50 text-blue-600 font-bold hover:bg-blue-100" :
                    "text-stone-700 hover:bg-gray-100 bg-transparent"}`}>
                {day}
              </button>
              {booked && !disabled && (
                <div className={`w-1.5 h-1.5 rounded-full mt-0.5 ${sel ? "bg-white" : "bg-blue-500"}`} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─── TIMEZONE SELECTOR ────────────────────────────────────────────────────────
const TimezoneSelect: FC<{ value: string; onChange: (tz: string) => void }> = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const selected = TIMEZONES.find(t => t.tz === value) ?? TIMEZONES[0];
  const currentTime = new Date().toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", timeZone: value });

  useEffect(() => {
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button onClick={() => setOpen(v => !v)}
        className="flex items-center gap-2 text-sm text-stone-700 font-medium bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-xl px-4 py-2.5 cursor-pointer transition-colors w-full">
        <GlobeIcon />
        <span className="flex-1 text-left">{selected.label} ({currentTime})</span>
        <ChevronDownIcon />
      </button>
      {open && (
        <div className="absolute bottom-full left-0 mb-2 bg-white border border-gray-100 rounded-2xl shadow-2xl w-full overflow-hidden z-50 py-1">
          {TIMEZONES.map(tz => (
            <button key={tz.tz} onClick={() => { onChange(tz.tz); setOpen(false); }}
              className={`flex items-center justify-between w-full px-4 py-2.5 text-sm border-none bg-transparent cursor-pointer transition-colors text-left
                ${value === tz.tz ? "bg-orange-50 text-orange-600 font-semibold" : "text-stone-700 hover:bg-gray-50"}`}>
              <span>{tz.label}</span>
              <span className="text-xs text-gray-400">{tz.offset}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── SCHEDULE BOOKING ─────────────────────────────────────────────────────────
const ScheduleTab: FC = () => {
  const [step, setStep]             = useState(1);
  const [sessionType, setSession]   = useState(SESSION_TYPES[0]);
  const [selectedDate, setDate]     = useState<Date | null>(null);
  const [selectedTime, setTime]     = useState<string | null>(null);
  const [timezone, setTimezone]     = useState(TIMEZONES[0].tz);
  const [form, setForm]             = useState<HelpCenterFormData>({ name: "", email: "", phone: "", note: "" });
  const [loading, setLoading]       = useState(false);
  const [confirmed, setConfirmed]   = useState(false);
  const [slots, setSlots]           = useState<HelpCenterTimeSlot[]>([]);
  const [bookedDays, setBookedDays] = useState<number[]>([]);
  const [slotLoading, setSlotLoading] = useState(false);
  const [slotError, setSlotError]   = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const canNext1 = selectedDate && selectedTime;
  const canNext2 = form.name.trim() && form.email.trim();

  useEffect(() => {
    if (!selectedDate) {
      setSlots([]);
      return;
    }

    const loadAvailability = async () => {
      setSlotLoading(true);
      setSlotError(null);
      try {
        const data = await getHelpCenterAvailability({
          date: selectedDate.toISOString().slice(0, 10),
          duration: sessionType.duration,
          timezone,
        });
        setSlots(data.slots);
        setBookedDays(data.bookedDays);
      } catch (error) {
        setSlots([]);
        setSlotError(error instanceof Error ? error.message : "Could not load available slots.");
      } finally {
        setSlotLoading(false);
      }
    };

    loadAvailability();
  }, [selectedDate, sessionType.duration, timezone]);

  const handleConfirm = async () => {
    if (!selectedDate || !selectedTime) return;

    setLoading(true);
    setSubmitError(null);
    try {
      await createHelpCenterSession({
        sessionType: sessionType.id,
        sessionLabel: sessionType.label,
        duration: sessionType.duration,
        date: selectedDate.toISOString().slice(0, 10),
        time: selectedTime,
        timezone,
        customer: form,
      });
      setConfirmed(true);
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : "Failed to book support session.");
    } finally {
      setLoading(false);
    }
  };

  if (confirmed) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="text-orange-500 mb-4"><CheckCircle /></div>
        <h2 className="m-0 text-2xl font-extrabold text-stone-900 mb-2">You're booked! 🎉</h2>
        <p className="m-0 text-gray-500 text-sm mb-1">
          <strong>{sessionType.label}</strong> · {sessionType.duration} min
        </p>
        <p className="m-0 text-gray-500 text-sm mb-6">
          {selectedDate?.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" })} at {selectedTime} ({TIMEZONES.find(t => t.tz === timezone)?.label})
        </p>
        <div className="bg-orange-50 border border-orange-100 rounded-2xl px-8 py-5 mb-6 max-w-xs">
          <p className="m-0 text-xs text-orange-700 font-medium">A confirmation with the video conference link will be sent to <strong>{form.email}</strong></p>
        </div>
        <button onClick={() => { setConfirmed(false); setStep(1); setDate(null); setTime(null); setForm({ name: "", email: "", phone: "", note: "" }); }}
          className="bg-stone-800 hover:bg-stone-700 text-white border-none rounded-2xl px-8 py-3 font-bold text-sm cursor-pointer transition-colors">
          Book Another Session
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Step indicator */}
      <div className="flex items-center justify-center gap-0 mb-8">
        {[1, 2, 3].map((s, i) => (
          <div key={s} className="flex items-center">
            <StepDot step={s} current={step} label={["Session","Date & Time","Details"][i]} />
            {i < 2 && <div className={`w-12 h-0.5 mb-5 mx-1 ${step > s ? "bg-orange-400" : "bg-gray-200"}`} />}
          </div>
        ))}
      </div>

      {/* STEP 1 – Choose session type */}
      {step === 1 && (
        <div>
          <h3 className="m-0 mb-1 text-xl font-bold text-stone-900">Choose a session type</h3>
          <p className="m-0 mb-6 text-sm text-gray-400">Select what you'd like to talk about</p>
          <div className="grid grid-cols-2 gap-3 mb-6">
            {SESSION_TYPES.map(s => (
              <button key={s.id} onClick={() => setSession(s)}
                className={`text-left p-4 rounded-2xl border-2 cursor-pointer transition-all
                  ${sessionType.id === s.id ? "border-orange-500 bg-orange-50" : "border-gray-200 bg-white hover:border-orange-200 hover:bg-orange-50/30"}`}>
                <p className="m-0 text-sm font-bold text-stone-900 mb-1">{s.label}</p>
                <p className="m-0 text-xs text-gray-400 leading-relaxed mb-2">{s.desc}</p>
                <div className="flex items-center gap-1.5 text-[11px] text-gray-400 font-medium">
                  <ClockIcon /><span>{s.duration} min</span>
                  <span className="mx-1 text-gray-200">·</span>
                  <VideoIcon /><span>Video call</span>
                </div>
              </button>
            ))}
          </div>
          <div className="flex justify-end">
            <button onClick={() => setStep(2)}
              className="bg-orange-500 hover:bg-orange-600 text-white border-none rounded-2xl px-8 py-3 font-bold text-sm cursor-pointer transition-colors">
              Next: Pick a time →
            </button>
          </div>
        </div>
      )}

      {/* STEP 2 – Date & Time */}
      {step === 2 && (
        <div className="grid grid-cols-[1fr_auto_1fr] gap-6 items-start">
          {/* Left: session info */}
          <div className="py-2">
            <div className="fle items-center gap-3 mb-5 pb-5 border-b border-gray-100">
              <div className={`flex items-center transition-all duration-300 cursor-pointer `}>
                <LogoIcon />
                <p className='Logo-sans font-semibold text-2xl text-[#ee5219]'>Gemio</p>
              </div>
              <div>
                <p className="m-0 text-base font-bold text-stone-900">{sessionType.label}</p>
              </div>
            </div>
            <div className="flex items-center gap-2.5 mb-3 text-sm text-gray-600">
              <ClockIcon /><span>{sessionType.duration} min</span>
            </div>
            <div className="flex items-start gap-2.5 mb-6 text-sm text-gray-600">
              <div className="mt-0.5 flex-shrink-0"><VideoIcon /></div>
              <span>Web conferencing details provided upon confirmation.</span>
            </div>
            <p className="m-0 text-sm text-gray-600 leading-relaxed">
              Book a session to get a live walkthrough of the Gemio reservation system and find out how we can help your restaurant thrive.
            </p>
          </div>

          {/* Divider */}
          <div className="w-px bg-gray-100 self-stretch" />

          {/* Right: Calendar + slots */}
          <div>
            <h3 className="m-0 mb-5 text-lg font-bold text-stone-900">Select a Date & Time</h3>
            <BookingCalendar selected={selectedDate} onSelect={d => { setDate(d); setTime(null); }} bookedDays={bookedDays} />

            {/* Time slots */}
            {selectedDate && (
              <div className="mt-5">
                <p className="m-0 mb-3 text-xs font-bold text-gray-400 uppercase tracking-widest">
                  {selectedDate.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "short" })}
                </p>
                {slotLoading && <p className="m-0 mb-3 text-xs text-gray-400">Loading available slots...</p>}
                {slotError && <p className="m-0 mb-3 text-xs text-red-500">{slotError}</p>}
                <div className="grid grid-cols-3 gap-2 max-h-48 overflow-y-auto pr-1">
                  {slots.map(s => (
                    <button key={s.time} onClick={() => s.available && setTime(s.time)} disabled={!s.available}
                      className={`py-2 rounded-xl border-2 text-sm font-semibold cursor-pointer transition-all
                        ${!s.available ? "border-gray-100 text-gray-300 cursor-not-allowed bg-gray-50" :
                          selectedTime === s.time ? "border-blue-600 bg-blue-600 text-white" :
                          "border-gray-200 bg-white text-stone-700 hover:border-blue-400 hover:bg-blue-50"}`}>
                      {s.time}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Timezone */}
            <div className="mt-5">
              <p className="m-0 mb-2 text-xs font-bold text-gray-400 uppercase tracking-widest">Time zone</p>
              <TimezoneSelect value={timezone} onChange={setTimezone} />
            </div>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="flex justify-between mt-6 pt-5 border-t border-gray-100">
          <button onClick={() => setStep(1)} className="bg-gray-100 hover:bg-gray-200 text-stone-700 border-none rounded-2xl px-6 py-2.5 font-semibold text-sm cursor-pointer transition-colors">
            ← Back
          </button>
          <button onClick={() => setStep(3)} disabled={!canNext1}
            className="bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white border-none rounded-2xl px-8 py-2.5 font-bold text-sm cursor-pointer transition-colors">
            Next: Your details →
          </button>
        </div>
      )}

      {/* STEP 3 – Contact details */}
      {step === 3 && (
        <div>
          <h3 className="m-0 mb-1 text-xl font-bold text-stone-900">Your details</h3>
          <p className="m-0 mb-6 text-sm text-gray-400">We'll send the confirmation to your email</p>

          {/* Booking summary */}
          <div className="bg-orange-50 border border-orange-100 rounded-2xl px-5 py-4 mb-6 flex items-start gap-4">
            <div className="text-2xl">{sessionType.icon}</div>
            <div className="flex-1">
              <p className="m-0 font-bold text-stone-900 text-sm">{sessionType.label}</p>
              <p className="m-0 text-xs text-gray-500 mt-0.5">
                {selectedDate?.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" })} · {selectedTime} · {sessionType.duration} min
              </p>
              <p className="m-0 text-xs text-orange-600 mt-0.5 font-medium">{TIMEZONES.find(t => t.tz === timezone)?.label}</p>
            </div>
            <button onClick={() => setStep(2)} className="text-xs text-orange-500 hover:text-orange-600 font-semibold bg-transparent border-none cursor-pointer p-0 transition-colors">
              Change
            </button>
          </div>

          <div className="flex flex-col gap-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="m-0 mb-1.5 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Full Name *</p>
                <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="Your name" className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm text-stone-800 bg-white outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-50 transition-all box-border" />
              </div>
              <div>
                <p className="m-0 mb-1.5 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Email *</p>
                <input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                  placeholder="you@example.com" className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm text-stone-800 bg-white outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-50 transition-all box-border" />
              </div>
            </div>
            <div>
              <p className="m-0 mb-1.5 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Phone (optional)</p>
              <input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                placeholder="+254 700 000 000" className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm text-stone-800 bg-white outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-50 transition-all box-border" />
            </div>
            <div>
              <p className="m-0 mb-1.5 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Additional notes</p>
              <textarea value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))} rows={3}
                placeholder="Anything you'd like us to know before the session..."
                className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm text-stone-800 bg-white outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-50 transition-all resize-y box-border" />
            </div>
          </div>

          <div className="flex justify-between mt-6 pt-5 border-t border-gray-100">
            <button onClick={() => setStep(2)} className="bg-gray-100 hover:bg-gray-200 text-stone-700 border-none rounded-2xl px-6 py-2.5 font-semibold text-sm cursor-pointer transition-colors">
              ← Back
            </button>
            <button onClick={handleConfirm} disabled={!canNext2 || loading}
              className="bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white border-none rounded-2xl px-8 py-2.5 font-bold text-sm cursor-pointer transition-colors flex items-center gap-2">
              {loading ? <><Spinner /> Confirming…</> : "Confirm Booking →"}
            </button>
          </div>
          {submitError && <p className="m-0 mt-3 text-sm text-red-500">{submitError}</p>}
        </div>
      )}
    </div>
  );
};

const ChatTab: FC = () => {
  const [messages, setMessages]   = useState<ChatMessage[]>([
    { id: "sys1", from: "system", text: "You're connected to Gemio Support", ts: new Date() },
    { id: "a1",   from: "agent",  text: "Hi there! 👋 Welcome to Gemio support. How can I help you today?", ts: new Date() },
  ]);
  const [input, setInput]         = useState("");
  const [typing, setTyping]       = useState(false);
  const [online]                  = useState(isWithinHours());
  const bottomRef                 = useRef<HTMLDivElement>(null);
  const inputRef                  = useRef<HTMLInputElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, typing]);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text) return;
    const userMsg: ChatMessage = { id: `u${Date.now()}`, from: "user", text, ts: new Date() };
    setMessages(m => [...m, userMsg]);
    setInput("");
    setTyping(true);
    try {
      const response = await sendHelpCenterChatMessage({ text, context: "general" });
      setMessages(m => [...m, { id: `a${Date.now()}`, from: "agent", text: response.reply, ts: new Date() }]);
    } catch (error) {
      setMessages(m => [...m, {
        id: `s${Date.now()}`,
        from: "system",
        text: error instanceof Error ? error.message : "Could not send message. Please try again.",
        ts: new Date(),
      }]);
    } finally {
      setTyping(false);
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Chat header */}
      <div className="flex items-center gap-4 p-5 bg-gradient-to-r from-stone-800 to-stone-700 rounded-2xl mb-4">
        <div className="relative">          
          {online && <div className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 bg-green-400 rounded-full border-2 border-stone-700" />}
        </div>
        <div className="flex-1">
          <p className="m-0 font-bold text-white text-base">Gemio Support</p>
          <p className="m-0 text-sm text-stone-300 mt-0.5">
            {online
              ? <span className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-green-400 inline-block" />We're available now</span>
              : <span className="text-stone-400">Outside business hours — we'll reply soon</span>
            }
          </p>
        </div>
        <div className="text-stone-300 flex items-center gap-1.5 text-sm">
          <HeadphonesIcon />
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto flex flex-col gap-3 min-h-[320px] max-h-[360px] px-1 mb-4">
        {messages.map(msg => {
          if (msg.from === "system") {
            return (
              <div key={msg.id} className="text-center">
                <span className="text-[11px] text-gray-400 bg-gray-100 px-3 py-1 rounded-full">{msg.text}</span>
              </div>
            );
          }
          const isUser = msg.from === "user";
          return (
            <div key={msg.id} className={`flex gap-2.5 ${isUser ? "flex-row-reverse" : "flex-row"}`}>
              {!isUser && (
                <div className="w-8 h-8 rounded-full bg-orange-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-white text-xs font-bold">G</span>
                </div>
              )}
              <div className={`max-w-[75%] px-4 py-3 rounded-2xl text-sm leading-relaxed
                ${isUser
                  ? "bg-stone-800 text-white rounded-tr-sm"
                  : "bg-white border border-gray-100 text-stone-800 shadow-sm rounded-tl-sm"}`}>
                {msg.text}
                <p className={`m-0 mt-1 text-[10px] ${isUser ? "text-stone-400 text-right" : "text-gray-400"}`}>
                  {msg.ts.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}
                </p>
              </div>
            </div>
          );
        })}

        {typing && (
          <div className="flex gap-2.5">
            <div className="w-8 h-8 rounded-full bg-orange-500 flex items-center justify-center flex-shrink-0">
              <span className="text-white text-xs font-bold">G</span>
            </div>
            <div className="bg-white border border-gray-100 shadow-sm px-4 py-3 rounded-2xl rounded-tl-sm">
              <div className="flex gap-1 items-center h-4">
                {[0,1,2].map(i => (
                  <div key={i} className="w-1.5 h-1.5 rounded-full bg-gray-400"
                    style={{ animation: `bounce 1.2s ease-in-out ${i*0.2}s infinite` }} />
                ))}
              </div>
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div className="flex items-center gap-2 bg-white border-2 border-gray-200 rounded-2xl p-2 focus-within:border-orange-400 transition-colors">
        <input ref={inputRef} value={input} onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === "Enter" && sendMessage()}
          placeholder="Type a message…"
          className="flex-1 px-2 py-1.5 text-sm text-stone-800 bg-transparent border-none outline-none placeholder-gray-300" />
        <button onClick={sendMessage} disabled={!input.trim()}
          className="w-10 h-10 rounded-xl bg-orange-500 hover:bg-orange-600 disabled:bg-gray-100 disabled:text-gray-300 text-white border-none cursor-pointer flex items-center justify-center transition-colors flex-shrink-0">
          <SendIcon />
        </button>
      </div>
      <style>{`@keyframes bounce { 0%,80%,100%{transform:translateY(0)} 40%{transform:translateY(-6px)} }`}</style>
    </div>
  );
};

// ─── CONTACT TAB ─────────────────────────────────────────────────────────────
const ContactTab: FC = () => {
  const online = isWithinHours();
  return (
    <div className="flex flex-col gap-5">
      {/* Phone */}
      <div className="bg-white border-2 border-gray-100 rounded-2xl overflow-hidden">
        <div className="px-6 py-5 flex items-start gap-4">
          <div className="w-11 h-11 bg-orange-50 rounded-xl flex items-center justify-center flex-shrink-0">
            <div className="text-orange-500"><PhoneIcon /></div>
          </div>
          <div className="flex-1">
            <p className="m-0 font-bold text-stone-900 text-base mb-0.5">Call us</p>
            <p className="m-0 text-sm text-gray-500 mb-3">We get it — some things are easier explained over the phone.</p>
            <a href={`tel:${BUSINESS.phone}`}
              className="text-lg font-bold text-orange-500 hover:text-orange-600 transition-colors no-underline">
              {BUSINESS.phone}
            </a>
          </div>
        </div>
      </div>

      {/* Email */}
      <div className="bg-white border-2 border-gray-100 rounded-2xl overflow-hidden">
        <div className="px-6 py-5 flex items-start gap-4">
          <div className="w-11 h-11 bg-stone-50 rounded-xl flex items-center justify-center flex-shrink-0">
            <div className="text-stone-600"><MailIcon /></div>
          </div>
          <div className="flex-1">
            <p className="m-0 font-bold text-stone-900 text-base mb-0.5">Email us</p>
            <p className="m-0 text-sm text-gray-500 mb-3">We'll get back to you within one business day.</p>
            <a href={`mailto:${BUSINESS.email}`}
              className="text-sm font-semibold text-stone-700 hover:text-orange-500 transition-colors no-underline break-all">
              {BUSINESS.email}
            </a>
          </div>
        </div>
      </div>

      {/* Opening hours */}
      <div className="bg-white border-2 border-gray-100 rounded-2xl overflow-hidden">
        <div className="px-6 py-5">
          <div className="flex items-center justify-between mb-4">
            <p className="m-0 font-bold text-stone-900 text-base">Opening Hours</p>
            <span className={`flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full ${online ? "bg-green-50 text-green-600" : "bg-gray-100 text-gray-500"}`}>
              <div className={`w-1.5 h-1.5 rounded-full ${online ? "bg-green-500" : "bg-gray-400"}`} />
              {online ? "Open now" : "Closed"}
            </span>
          </div>

          <div className="flex flex-col gap-3">
            {/* Weekdays */}
            <div className="bg-gray-50 rounded-xl px-4 py-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-bold text-stone-900">{BUSINESS.hours.weekdays.label}</span>
              </div>
              <div className="flex items-center gap-3 text-sm text-gray-600">
                <div className="flex flex-col">
                  <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-0.5">Nairobi (EAT)</span>
                  <span className="font-semibold text-stone-800">{BUSINESS.hours.weekdays.nairobi}</span>
                </div>
                <div className="w-px h-8 bg-gray-200" />
                <div className="flex flex-col">
                  <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-0.5">CET</span>
                  <span className="font-semibold text-stone-700">{BUSINESS.hours.weekdays.cet}</span>
                </div>
              </div>
            </div>

            {/* Saturday */}
            <div className="bg-gray-50 rounded-xl px-4 py-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-bold text-stone-900">{BUSINESS.hours.saturday.label}</span>
              </div>
              <div className="flex items-center gap-3 text-sm text-gray-600">
                <div className="flex flex-col">
                  <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-0.5">Nairobi (EAT)</span>
                  <span className="font-semibold text-stone-800">{BUSINESS.hours.saturday.nairobi}</span>
                </div>
                <div className="w-px h-8 bg-gray-200" />
                <div className="flex flex-col">
                  <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-0.5">CET</span>
                  <span className="font-semibold text-stone-700">{BUSINESS.hours.saturday.cet}</span>
                </div>
              </div>
            </div>

            {/* Sunday */}
            <div className="bg-red-50 rounded-xl px-4 py-3 flex items-center justify-between">
              <span className="text-sm font-semibold text-stone-700">Sunday</span>
              <span className="text-sm font-bold text-red-400">Closed</span>
            </div>
          </div>
        </div>
      </div>

      {/* Quick chat CTA */}
      <div className="bg-gradient-to-br from-stone-800 to-stone-700 rounded-2xl px-6 py-5 flex items-center gap-4">
        <div className="flex-1">
          <p className="m-0 text-white font-bold text-base mb-1">Chat with us</p>
          <p className="m-0 text-stone-300 text-sm">
            {online ? "We're available now — get an instant response." : "Leave a message and we'll reply first thing."}
          </p>
        </div>
        <div className="flex-shrink-0">
          {online && <div className="w-2 h-2 rounded-full bg-green-400 mb-1 ml-auto" />}
          <div className="text-orange-400"><ChatIcon size={28} /></div>
        </div>
      </div>
    </div>
  );
};

// ─── FLOATING CHAT BUTTON ────────────────────────────────────────────────────
const FloatingChatButton: FC<{ onClick: () => void; online: boolean }> = ({ onClick, online }) => (
  <button onClick={onClick}
    className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-orange-500 hover:bg-orange-600 text-white rounded-full shadow-2xl border-none cursor-pointer flex items-center justify-center transition-all hover:scale-105 active:scale-95">
    <ChatIcon size={24} />
    {online && <div className="absolute top-0.5 right-0.5 w-3 h-3 bg-green-400 rounded-full border-2 border-white" />}
  </button>
);

// ─── MAIN HELP CENTER ─────────────────────────────────────────────────────────
const HelpCenter: FC = () => {
  const [activeTab, setActiveTab] = useState<HelpTab>("schedule");
  const [showChat, setShowChat]   = useState(false);
  const online = !isWithinHours();

  const tabs: { id: HelpTab; label: string; icon: ReactNode; desc: string }[] = [
    { id: "schedule", label: "Book a Session",    icon: <CalendarIcon />, desc: "Schedule a call or demo" },
    { id: "chat",     label: "Chat with Us",      icon: <ChatIcon />,     desc: "Talk to a human" },
    { id: "contact",  label: "Contact",           icon: <PhoneIcon />,    desc: "Hours & details" },
  ];

  return (
    <>
      <div className="min-h-screen bg-slate-100">
        <div className="max-w-4xl mx-auto px-2 md:px-4 py-4 md:py-10">
          {/* ── HERO ──────────────────────────────────────────────── */}
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 bg-orange-50 border border-orange-100 rounded-full px-4 py-1.5 text-xs font-semibold text-orange-600 mb-4">
              <div className={`w-1.5 h-1.5 rounded-full ${online  ? "bg-green-500" : "bg-gray-400"}`} />
              {online ? "Support team online now" : "Support — replies within 1 business day"}
            </div>
            <h1 className="m-0 text-4xl font-extrabold text-stone-900 mb-3" style={{ fontFamily: "'DM Serif Display', serif" }}>
              How can we help?
            </h1>
            <p className="m-0 text-gray-500 text-base max-w-md mx-auto leading-relaxed">
              Book a live session, chat with a specialist, or find our contact details below.
            </p>
          </div>

          {/* ── TAB SWITCHER ──────────────────────────────────────── */}
          <div className="flex gap-3 mb-6 bg-white rounded-2xl p-2 shadow-sm border border-gray-100">
            {tabs.map(tab => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-xl border-none cursor-pointer transition-all text-sm font-semibold
                  ${activeTab === tab.id
                    ? "bg-orange-500 text-white shadow-sm"
                    : "bg-transparent text-stone-500 hover:text-stone-800 hover:bg-gray-50"}`}>
                {tab.icon}
                <span className="hidden sm:inline">{tab.label}</span>
              </button>
            ))}
          </div>

          {/* ── CONTENT CARD ──────────────────────────────────────── */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-2 md:p-8 min-h-[500px]">
            {activeTab === "schedule" && <ScheduleTab />}
            {activeTab === "chat"     && (
              <div>
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <h2 className="m-0 text-xl font-bold text-stone-900 mb-1">Chat with us</h2>
                    <p className="m-0 text-sm text-gray-400">
                      {online
                        ? "We're available now — a human will respond shortly."
                        : "Leave us a message and we'll be in touch during business hours."}
                    </p>
                  </div>
                  <span className={`flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full flex-shrink-0
                    ${online ? "bg-green-50 text-green-600" : "bg-gray-100 text-gray-500"}`}>
                    <div className={`w-1.5 h-1.5 rounded-full ${online ? "bg-green-500" : "bg-gray-400"}`} />
                    {online ? "Online" : "Offline"}
                  </span>
                </div>
                <ChatTab />
              </div>
            )}
            {activeTab === "contact" && (
              <div>
                <h2 className="m-0 text-xl font-bold text-stone-900 mb-1">Get in touch</h2>
                <p className="m-0 text-sm text-gray-400 mb-6">Multiple ways to reach our team.</p>
                <ContactTab />
              </div>
            )}
          </div>

          {/* ── FOOTER ────────────────────────────────────────────── */}
          <div className="text-center mt-8 flex items-center justify-center gap-4 text-xs text-gray-400 flex-wrap">
            <button className="bg-transparent border-none cursor-pointer text-gray-400 hover:text-orange-500 transition-colors text-xs font-medium p-0">
              Cookie settings
            </button>
            <span className="text-gray-200">·</span>
            <span>© {new Date().getFullYear()} Gemio. All rights reserved.</span>
          </div>
        </div>
      </div>

      {/* ── FLOATING CHAT BUTTON ──────────────────────────────────── */}
      {activeTab !== "chat" && <FloatingChatButton online={online} onClick={() => setActiveTab("chat")} />}
    </>
  );
};

export default HelpCenter;